import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { expertService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Video, Mic, MessageCircle, Clock, AlertCircle, FileText, ExternalLink } from 'lucide-react';

const STATUS_TABS = ['ALL', 'SCHEDULED', 'LIVE', 'COMPLETED', 'CANCELLED'];
const sessionIcon = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };
const statusStyle = {
  SCHEDULED: 'bg-amber-50 text-amber-700 border-amber-200',
  LIVE:      'bg-green-50 text-green-700 border-green-200',
  COMPLETED: 'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED: 'bg-cream-100 text-forest-400 border-cream-200',
};

const ExpertSessionsPage = () => {
  const [activeTab, setActiveTab] = useState('ALL');
  const params = { page: 1, limit: 20 };
  if (activeTab !== 'ALL') params.status = activeTab;

  const { data, loading, error } = useApi(() => expertService.getMySessions(params), [activeTab]);
  const sessions = Array.isArray(data) ? data
    : Array.isArray(data?.sessions) ? data.sessions
    : Array.isArray(data?.data) ? data.data : [];

  return (
    <DashboardLayout title="My Sessions">
      <div className="max-w-3xl space-y-5">
        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`text-xs font-medium px-4 py-2 rounded-full border transition-all ${activeTab === t ? 'bg-forest-800 text-cream-50 border-forest-800' : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'}`}>
              {t}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load sessions.</span>
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
            <Video size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No sessions yet</p>
            <p className="text-sm mt-2">Your confirmed sessions will appear here</p>
          </div>
        )}

        <div className="space-y-3">
          {loading ? Array.from({length: 3}).map((_, i) => (
            <div key={i} className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse h-24" />
          )) : sessions.map(session => {
            const Icon = sessionIcon[session.sessionType] ?? Video;

            const clientParticipant = (session.participants ?? []).find(p => p.clientProfile?.user);
            const clientName = clientParticipant
              ? `${clientParticipant.clientProfile.user.firstName} ${clientParticipant.clientProfile.user.lastName}`.trim()
              : 'Client';

            const date = session.booking?.scheduledAt
              ? new Date(session.booking.scheduledAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' })
              : '—';

            return (
              <div key={session.id} className="bg-white border border-cream-200 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon size={17} className="text-forest-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h4 className="font-sans font-semibold text-forest-900 text-sm">{session.sessionType} with {clientName}</h4>
                        <div className="flex items-center gap-3 mt-1 text-xs text-forest-500">
                          <span>{date}</span>
                          {session.duration && <span className="flex items-center gap-1"><Clock size={10} /> {session.duration} mins</span>}
                        </div>
                        {session.transcript?.status && (
                          <span className="text-xs text-forest-400 mt-1 block">Transcript: {session.transcript.status}</span>
                        )}
                      </div>
                      <span className={`text-[10px] font-medium border px-2.5 py-1 rounded-full flex-shrink-0 ${statusStyle[session.status] ?? ''}`}>
                        {session.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100 flex-wrap">
                  {/* Rejoin live session */}
                  {session.status === 'LIVE' && (
                    <Link to={`/sessions/${session.id}`} className="btn-primary text-xs py-1.5 px-4">
                      Rejoin Session →
                    </Link>
                  )}

                  {/* View recording & transcript */}
                  {session.status === 'COMPLETED' && (
                    <Link
                      to={`/expert/sessions/${session.id}/recording`}
                      className="flex items-center gap-1.5 text-xs border border-blue-200 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-full transition-colors font-medium"
                    >
                      <ExternalLink size={12} />
                      {session.recordingUrl ? 'View Recording & Transcript' : 'View Session Details'}
                    </Link>
                  )}

                  {/* Download transcript PDF if available */}
                  {session.status === 'COMPLETED' && session.transcript?.pdfUrl && (
                    <a href={session.transcript.pdfUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors font-medium">
                      <FileText size={12} /> Download Transcript
                    </a>
                  )}

                  {/* Recording still processing */}
                  {session.status === 'COMPLETED' && !session.recordingUrl && (
                    <span className="text-xs text-forest-400 italic">⏳ Recording processing...</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ExpertSessionsPage;